import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import WorldMap from "@/components/WorldMap";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import AscentVisual from "@/components/AscentVisual";
import OutcomesGrid from "@/components/OutcomesGrid";
import { shouldPlayIntro, INTRO_S } from "@/components/BrandIntro";
import { SERVICES, SECTORS, WHY_THRIVE, FRACTIONAL, CALENDLY_URL } from "@/content";
import { INSIGHTS } from "@/insights";

const H1_LINES = ["Strategic Business Development", "for Energy and Technical Markets"];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const { scrollY } = useScroll();
  const mapY = useTransform(scrollY, [0, 700], [0, 130]);
  const [intro] = useState(() => (shouldPlayIntro() ? INTRO_S - 0.3 : 0));

  return (
    <div data-testid="home-page">
      <SEO
        title="Thrive Business Development | Strategic Business Development for Energy & Technical Markets"
        description="Intelligence-led business development for energy, engineering and technical B2B markets. Market mapping, target account identification and decision-maker research."
      />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <motion.div style={{ y: mapY }} className="absolute inset-0">
          <WorldMap className="h-full w-full" />
        </motion.div>
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0B0F17_78%)]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-28 sm:pt-48 sm:pb-36">
          <motion.p
            className="eyebrow mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: intro + 0.15, duration: 0.8 }}
          >
            Strategic Business Development · Market Intelligence
          </motion.p>
          <h1 className="font-display max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
            {H1_LINES.map((line, i) => (
              <MaskedLine key={line} delay={intro + 0.25 + i * 0.18}>
                {i === 1 ? <span className="text-slate-300">{line}</span> : line}
              </MaskedLine>
            ))}
          </h1>
          <motion.p
            className="mt-8 max-w-2xl text-lg sm:text-xl text-slate-400 leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: intro + 0.8, duration: 0.8 }}
          >
            We help businesses identify the right markets, target the right companies, and connect
            with the right decision-makers through intelligence-led business development.
          </motion.p>
          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: intro + 0.95, duration: 0.8 }}
          >
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-consultation-button"
              className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              Book a Consultation
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/services"
              data-testid="hero-explore-services-button"
              className="group inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-slate-200 transition-all duration-300 hover:border-electric/60 hover:text-electric"
            >
              Explore Services
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* SECTION 2: INTELLIGENCE */}
      <section className="py-20 lg:py-32" data-testid="home-intelligence-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <p className="eyebrow-blue mb-6">Better Market Intelligence</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] leading-tight text-white">
                Commercial Growth Built on Better Intelligence
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-7 text-base text-slate-400 leading-relaxed">
                Thrive Business Development supports companies operating across energy and technical
                sectors with market mapping, business development strategy, target account
                identification and decision-maker research.
              </p>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed">
                We help you focus commercial effort where it matters most.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 flex items-center gap-6 font-mono text-[11px] uppercase tracking-[0.25em]">
                <span className="text-slate-500">Universe</span>
                <span className="h-px flex-1 bg-gradient-to-r from-white/20 via-electric/40 to-gold/60" aria-hidden="true" />
                <span className="text-gold">Focus</span>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <AscentVisual />
          </Reveal>
        </div>
      </section>

      {/* SECTION 3: SERVICES */}
      <section className="py-20 lg:py-32 border-t border-white/10" data-testid="home-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow mb-6">What We Do</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-white">
                Our Services
              </h2>
            </div>
            <span className="font-mono text-xs tracking-[0.25em] text-slate-500">01 / 05</span>
          </Reveal>

          <div className="mt-14 border-t border-white/10">
            {[SERVICES[0], SERVICES[1], SERVICES[2], SERVICES[5], FRACTIONAL].map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                <Link
                  to={`/services#${s.id}`}
                  data-testid={`home-service-${s.id}`}
                  className="group grid gap-4 border-b border-white/10 py-9 transition-colors duration-300 hover:bg-white/[0.02] sm:grid-cols-[70px_1fr_auto] sm:items-center sm:gap-8 sm:px-4"
                >
                  <span className="font-mono text-sm text-gold/70">{s.num}</span>
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-medium tracking-[-0.03em] text-white transition-colors duration-300 group-hover:text-gold">
                      {s.title}
                    </h3>
                    <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">{s.short}</p>
                  </div>
                  <ArrowUpRight
                    size={20}
                    className="hidden text-slate-600 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1 sm:block"
                  />
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-12">
            <Link
              to="/services"
              data-testid="home-explore-services-cta"
              className="group inline-flex items-center gap-2 border border-gold/50 px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-navy"
            >
              Explore Our Services
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTION 4: OUTCOMES */}
      <section className="py-20 lg:py-32 border-t border-white/10 dot-grid-bg" data-testid="home-outcomes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow-blue mb-6">Outcomes</p>
              <h2 className="font-display max-w-2xl text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-white">
                Clearer markets. Better opportunities. Stronger commercial decisions.
              </h2>
            </div>
            <p className="max-w-sm text-sm text-slate-400 leading-relaxed">What better business development should deliver.</p>
          </Reveal>
          <div className="mt-14">
            <OutcomesGrid />
          </div>
          <Reveal delay={0.15} className="mt-14">
            <Link
              to="/outcomes"
              data-testid="home-outcomes-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-electric transition-colors hover:text-white"
            >
              Explore the outcomes
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTION 5: SECTORS */}
      <section className="py-20 lg:py-32 border-t border-white/10" data-testid="home-sectors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="eyebrow mb-6">Where We Operate</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-white">
              Sectors We Understand
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed">
              We work across complex B2B markets where trust, timing, relationships and accurate
              market intelligence matter.
            </p>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 gap-x-16 border-t border-white/10">
            {SECTORS.map((s, i) => (
              <Reveal key={s.name} delay={(i % 2) * 0.06}>
                <Link
                  to={`/sectors#${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  data-testid={`home-sector-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex items-baseline justify-between gap-6 border-b border-white/10 py-5 transition-colors hover:bg-white/[0.02]"
                >
                  <span className="flex items-baseline gap-5">
                    <span className="font-mono text-xs text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display text-lg text-slate-200 transition-colors duration-300 group-hover:text-gold">
                      {s.name}
                    </span>
                  </span>
                  <ArrowUpRight size={16} className="shrink-0 text-slate-700 transition-all duration-300 group-hover:text-gold" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: WHY THRIVE */}
      <section className="py-20 lg:py-32 border-t border-white/10 bg-navy-light/40" data-testid="home-why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow-blue mb-6">The Difference</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-white">
              Why Thrive
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-white/10 border border-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_THRIVE.map((w, i) => (
              <Reveal key={w.num} delay={i * 0.07} className="h-full">
                <div className="flex h-full flex-col bg-navy p-8 transition-colors duration-500 hover:bg-navy-elevated">
                  <span className="font-mono text-xs text-gold/70">{w.num}</span>
                  <h3 className="mt-6 font-display text-lg font-medium uppercase tracking-wide text-white">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6b: INSIGHTS TEASER */}
      <section className="py-20 lg:py-32 border-t border-white/10" data-testid="home-insights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow mb-6">Insights</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] text-white">
                Latest Commentary
              </h2>
            </div>
            <Link
              to="/insights"
              data-testid="home-insights-all-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-electric transition-colors hover:text-white"
            >
              All insights
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="mt-14 grid gap-12 border-t border-white/10 pt-12 lg:grid-cols-[1.5fr_1fr] lg:gap-20">
            <Reveal>
              <Link
                to={`/insights/${INSIGHTS[0].slug}`}
                data-testid={`home-insight-${INSIGHTS[0].slug}`}
                className="group block"
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                  <span className="text-gold">{INSIGHTS[0].category}</span>
                  <span>{INSIGHTS[0].date}</span>
                  <span>{INSIGHTS[0].readTime}</span>
                </div>
                <h3 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] leading-tight text-white transition-colors duration-300 group-hover:text-gold">
                  {INSIGHTS[0].title}
                </h3>
                <p className="mt-6 max-w-2xl text-base text-slate-400 leading-relaxed">{INSIGHTS[0].dek}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-electric transition-colors group-hover:text-white">
                  Read the commentary
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>

            <div className="flex flex-col divide-y divide-white/10 border-t border-white/10 lg:border-t-0">
              {INSIGHTS.slice(1, 3).map((a, i) => (
                <Reveal key={a.slug} delay={0.1 + i * 0.08}>
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`home-insight-${a.slug}`}
                    className="group flex items-start justify-between gap-6 py-7 first:pt-7 lg:first:pt-0"
                  >
                    <div>
                      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-electric/80">{a.category}</span>
                      <h3 className="mt-3 font-display text-lg sm:text-xl font-bold tracking-[-0.03em] leading-snug text-white transition-colors duration-300 group-hover:text-gold">
                        {a.title}
                      </h3>
                      <span className="mt-3 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                        {a.date} · {a.readTime}
                      </span>
                    </div>
                    <ArrowUpRight size={18} className="mt-1 shrink-0 text-slate-600 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: ABOUT TEASER */}
      <section className="py-20 lg:py-32 border-t border-white/10" data-testid="home-about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-[1fr_auto] lg:items-end">
          <Reveal className="max-w-3xl">
            <p className="eyebrow mb-6">About Thrive</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[-0.03em] leading-tight text-white">
              A More Intelligent Approach to Growth
            </h2>
            <p className="mt-7 text-base text-slate-400 leading-relaxed">
              Thrive Business Development was created to help businesses approach growth with greater
              clarity.
            </p>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              We combine market understanding, structured research and practical business development
              experience to help clients identify opportunities, prioritise target accounts and reach
              the people who matter.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <Link
              to="/about"
              data-testid="home-about-cta"
              className="group inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-slate-200 transition-all duration-300 hover:border-gold/60 hover:text-gold"
            >
              About Thrive
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-white/10 py-24 lg:py-36" data-testid="home-final-cta">
        <div className="absolute inset-0 dot-grid-bg opacity-60" aria-hidden="true" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-gold/10 blur-[120px]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-[-0.03em] leading-tight text-white">
              Ready to Build a Smarter Business Development Strategy?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-xl text-lg text-slate-400 leading-relaxed">
              If you want a clearer view of your market, target clients and commercial opportunities,
              let's talk.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-final-cta-button"
              className="group mt-10 inline-flex items-center gap-2 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              Book a Consultation
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
